/**
 * Price label/extra captions: resolve copy with **account language first, then English only**.
 * Form slots always expose `en` plus the account language key when it is not `en`.
 */

const BLOB_META_KEYS = new Set([
  "labelkey",
  "languagespresent",
  "translationid",
  "translation_id",
]);

export function normalizeLangIso(raw: string | undefined | null): string {
  return (raw || "en").trim().toLowerCase().split("-")[0] || "en";
}

export function sanitizePriceCaption(candidate: string | undefined | null): string {
  if (candidate == null) return "";
  const s = String(candidate).trim();
  if (s.length === 0) return "";
  const slug = s.toLowerCase();
  if (slug === "title" || slug === "description" || slug === "label") return "";
  return s;
}

function rowLangIso(entry: unknown): string {
  if (entry == null || typeof entry !== "object") return "";
  const o = entry as Record<string, unknown>;
  const raw = String(
    o.languageIsoCode ??
      o.languageISO ??
      o.iso ??
      o.language ??
      o.lang ??
      ""
  ).trim();
  return raw ? normalizeLangIso(raw) : "";
}

function rowDisplayText(entry: unknown): string {
  if (entry == null) return "";
  if (typeof entry === "string" || typeof entry === "number") return String(entry).trim();
  if (typeof entry !== "object") return "";
  const o = entry as Record<string, unknown>;
  const raw =
    o.text ??
    o.label ??
    o.translation ??
    o.shortLabel ??
    o.short_label ??
    o.longText ??
    o.long_text ??
    o.name ??
    o.title ??
    o.caption ??
    o.value ??
    o.Value ??
    "";
  return String(raw).trim();
}

function ingestRowsIntoMap(rows: unknown[] | undefined, map: Record<string, string>): void {
  if (!Array.isArray(rows)) return;
  for (const row of rows) {
    const iso = rowLangIso(row);
    const t = sanitizePriceCaption(rowDisplayText(row));
    if (iso && t) map[iso] = t;
  }
}

/** Flat object / DWS `translations` / array rows → normalized lang → text */
export function langMapFromTranslationsBlob(blob: unknown): Record<string, string> {
  const map: Record<string, string> = {};
  if (blob == null || blob === "") return map;
  if (Array.isArray(blob)) {
    ingestRowsIntoMap(blob, map);
    return map;
  }
  if (typeof blob !== "object") return map;
  const o = blob as Record<string, unknown>;
  if (Array.isArray(o.translations)) {
    ingestRowsIntoMap(o.translations as unknown[], map);
  }
  for (const [k, val] of Object.entries(o)) {
    const lk = k.toLowerCase();
    if (BLOB_META_KEYS.has(lk) || lk === "translations") continue;
    if (typeof val === "string") {
      const iso = normalizeLangIso(k);
      const t = sanitizePriceCaption(val);
      if (t) map[iso] = map[iso] || t;
    }
  }
  return map;
}

/** Merge language → text from one experience/reservation price row. */
export function langMapFromPriceRow(priceItem: unknown): Record<string, string> {
  const map: Record<string, string> = {};
  const pi = priceItem as Record<string, unknown> | null | undefined;
  if (!pi || typeof pi !== "object") return map;

  const titlesRaw = pi.titles ?? pi.Titles;
  ingestRowsIntoMap(Array.isArray(titlesRaw) ? (titlesRaw as unknown[]) : undefined, map);

  const nestedBag = pi.titleTranslations ?? pi.title_translations;
  if (nestedBag != null && typeof nestedBag === "object") {
    Object.assign(map, langMapFromTranslationsBlob(nestedBag));
  }
  return map;
}

/**
 * Display resolution order: explicit locale if provided, else account language, then **English only** (no IT/DE chain).
 */
export function priceCaptionPickOrder(accountLang?: string | null, explicitLocale?: string): string[] {
  const base = normalizeLangIso(
    explicitLocale !== undefined && String(explicitLocale).trim() !== ""
      ? String(explicitLocale)
      : accountLang ?? "en"
  );
  return base === "en" ? ["en"] : [base, "en"];
}

export function captionFromLangMap(map: Record<string, string>, order: string[]): string {
  for (const iso of order) {
    const key = normalizeLangIso(iso);
    const s = sanitizePriceCaption(map[key]);
    if (s) return s;
  }
  return "";
}

export function captionFromPriceRow(priceItem: unknown, order: string[]): string {
  return captionFromLangMap(langMapFromPriceRow(priceItem), order);
}

/** `en` column for payloads: prefer explicit EN, else same fallback as UI (account → en). */
export function englishSlotText(map: Record<string, string>, accountLangIso: string): string {
  const fromEn = sanitizePriceCaption(map["en"]);
  if (fromEn) return fromEn;
  return captionFromLangMap(map, priceCaptionPickOrder(accountLangIso));
}

/** Filled slots for reactive forms: always `en`; add account key when ≠ `en`. */
export function fillTitleSlotsFromMergedMap(map: Record<string, string>, accountLangIso: string): Record<string, string> {
  const cur = normalizeLangIso(accountLangIso);
  const order = priceCaptionPickOrder(accountLangIso);
  const curText = captionFromLangMap(map, order);
  const enText = sanitizePriceCaption(map["en"]) || curText;
  const out: Record<string, string> = { en: enText };
  if (cur !== "en") {
    out[cur] = curText || enText;
  }
  return out;
}

export function fillPriceTitleSlots(priceItem: unknown, accountLangIso: string): Record<string, string> {
  return fillTitleSlotsFromMergedMap(langMapFromPriceRow(priceItem), accountLangIso);
}
